import React, { useState, useEffect } from 'react';
import { RFQComparisonModalProps, SupplierQuote } from '../types';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currencyUtils';
import { API_ENDPOINTS } from '../utils/config';
import '../styles/RFQComparisonModal.css';

const RFQComparisonModal: React.FC<RFQComparisonModalProps> = ({ 
  isOpen, 
  onClose, 
  request, 
  suppliers, 
  onSelectSupplier 
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [supplierDocuments, setSupplierDocuments] = useState<{[key: string]: any[]}>({});
  
  // Check if this is a final phase request with selected suppliers
  const isFinalPhase = request?.status === 'final_phase';
  const isSelectSupplierPhase = request?.status === 'approved' || request?.status === 'select_supplier';
  const isReadOnlyMode = true; // User can no longer select suppliers - read-only mode
  const availableSuppliers = request?.selected_suppliers || [];
  
  // Fetch documents for each supplier product from new collection
  useEffect(() => {
    const fetchSupplierDocuments = async () => {
      if (!isOpen || !token) return;
      
      const documentsMap: {[key: string]: any[]} = {};
      
      // For selected suppliers, fetch documents from new collection
      if (isSelectSupplierPhase || isFinalPhase) {
        for (const supplier of availableSuppliers) {
          const supplierProductId = supplier._id || supplier.product_id || supplier.supplier_id;
          if (supplierProductId) {
            try {
              const response = await fetch(API_ENDPOINTS.GET_SUPPLIER_PRODUCT_DOCUMENTS(supplierProductId), {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              
              if (response.ok) {
                const result = await response.json();
                if (result.success) {
                  documentsMap[supplierProductId] = result.data || [];
                }
              }
            } catch (error) {
              console.error(`Error fetching documents for supplier ${supplierProductId}:`, error);
            }
          }
        }
      }
      
      setSupplierDocuments(documentsMap);
    };

    fetchSupplierDocuments();
  }, [isOpen, token, isSelectSupplierPhase, isFinalPhase, availableSuppliers]);

  // Initialize selected supplier if empty
  useEffect(() => {
    if (isSelectSupplierPhase && !selectedSupplier) {
      setSelectedSupplier(null);
    }
  }, [isSelectSupplierPhase, availableSuppliers]);
  
  if (!isOpen || !request) return null;

  console.log('🔍 RFQComparisonModal - request:', request);
  console.log('🔍 RFQComparisonModal - suppliers:', suppliers);
  
  console.log('🔍 RFQComparisonModal - isFinalPhase:', isFinalPhase);
  console.log('🔍 RFQComparisonModal - isSelectSupplierPhase:', isSelectSupplierPhase);
  console.log('🔍 RFQComparisonModal - selectedSupplier:', selectedSupplier);


  const renderStars = (rating: number) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <span 
            key={star} 
            className={`star ${star <= rating ? 'filled' : 'empty'}`}
          >
            ★
          </span>
        ))}
        <span className="rating-text">({rating}/5)</span>
      </div>
    );
  };

  // Parse price function for Turkish number format
  const parsePrice = (priceValue: any): number => {
    if (typeof priceValue === 'number') {
      return priceValue;
    }
    
    if (typeof priceValue === 'string') {
      // Remove currency symbols and text
      let cleaned = priceValue.replace(/[^\d.,]/g, '');
      
      // Special handling for Turkish number format (11.208 -> 11208)
      if (/^\d+\.\d{3}$/.test(cleaned)) {
        cleaned = cleaned.replace('.', '');
        console.log('🔍 RFQComparisonModal - Detected thousands separator format:', priceValue, '->', cleaned);
      } else if (cleaned.includes(',') && cleaned.includes('.')) {
        // Both comma and dot - assume comma is thousands separator
        cleaned = cleaned.replace(/,/g, '');
      } else if (cleaned.includes(',')) {
        // Only comma - could be decimal or thousands separator
        const parts = cleaned.split(',');
        if (parts.length === 2 && parts[1].length > 3) {
          cleaned = parts.join(''); // Thousands separator
        } else {
          cleaned = cleaned.replace(',', '.'); // Decimal separator
        }
      }
      
      return parseFloat(cleaned) || 0;
    }
    
    return 0;
  };

  // For final phase and select supplier phase, use selected suppliers from admin
  const displaySuppliers = (isFinalPhase || isSelectSupplierPhase) ? availableSuppliers : suppliers;
  
  // Supplier selection functions removed - read-only mode

  const comparisonData = [
    {
      label: t('common.supplier_name'),
      key: 'supplier_name' as string,
      getValue: (supplier: any) => supplier.supplier_name || supplier.company_name || supplier.name,
      type: 'text'
    },
    {
      label: t('common.product_name'),
      key: 'product_name' as string,
      getValue: (supplier: any) => supplier.product_name,
      type: 'text'
    },
    {
      label: t('common.product_brand'),
      key: 'product_brand' as string,
      getValue: (supplier: any) => supplier.product_brand || 'N/A',
      type: 'text'
    },
    {
      label: t('common.price'),
      key: 'unit_price' as string,
      getValue: (supplier: any) => {
        const price = parsePrice(supplier.unit_price || supplier.price || 0);
        const currency = supplier.currency || 'TRY';
        return formatCurrency(price, currency);
      },
      type: 'currency'
    },
    {
      label: t('common.currency'),
      key: 'currency' as string,
      getValue: (supplier: any) => {
        const rawCurrency = supplier.currency || 'TRY';
        // Map common currency abbreviations
        const currencyMap: { [key: string]: string } = {
          'TL': 'TRY',
          'USD': 'USD',
          'EUR': 'EUR',
          'CHF': 'CHF',
          'GBP': 'GBP'
        };
        return currencyMap[rawCurrency] || rawCurrency;
      },
      type: 'text'
    },
    {
      label: t('common.supplier_email'),
      key: 'supplier_email' as string,
      getValue: (supplier: any) => supplier.supplier_email || supplier.email,
      type: 'text'
    },
    {
      label: t('common.supplier_region'),
      key: 'supplier_region' as string,
      getValue: (supplier: any) => supplier.supplier_region || 'N/A',
      type: 'text'
    },
    {
      label: t('common.rating'),
      key: 'rating' as string,
      getValue: (supplier: any) => {
        const rating = supplier.rating || 4; // Default 4 stars if no rating
        return renderStars(rating);
      },
      type: 'stars'
    },
    {
      label: t('common.attached_documents'),
      key: 'attached_documents' as string,
      getValue: (supplier: any) => {
        // For selected suppliers, show request-level attached_documents only for matching supplier
        if (isSelectSupplierPhase || isFinalPhase) {
          const requestDocs = request?.attached_documents || [];
          const supplierId = supplier._id || supplier.supplier_id;
          
          // Filter documents that belong to this specific supplier
          const supplierDocs = requestDocs.filter((doc: any) => 
            doc.supplier_id === supplierId
          );
          
          console.log('🔍 RFQComparisonModal - Supplier:', supplier.company_name, 'ID:', supplierId, 'Docs:', supplierDocs);
          return supplierDocs;
        }
        
        // For other phases, use supplier's own documents (fallback to old structure)
        return supplier.attached_documents || [];
      },
      type: 'documents'
    }
  ];

  // Filter out rows where all suppliers have N/A values
  const filteredComparisonData = comparisonData.filter(metric => {
    // Check if all suppliers have N/A values for this metric
    const allValuesNA = displaySuppliers.every(supplier => {
      const value = metric.getValue(supplier);
      return value === 'N/A' || value === '' || value === null || value === undefined || 
             (Array.isArray(value) && value.length === 0) ||
             (typeof value === 'string' && value.toLowerCase().includes('no documents'));
    });
    
    // Only show the row if not all values are N/A
    return !allValuesNA;
  });

  console.log('🔍 RFQComparisonModal - Original metrics:', comparisonData.length);
  console.log('🔍 RFQComparisonModal - Filtered metrics:', filteredComparisonData.length);

  return (
    <div className="rfq-modal-overlay" onClick={onClose}>
      <div className="rfq-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="rfq-modal-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="rfq-modal-header">
          <h2>
            {isFinalPhase ? t('common.final_phase_approved_suppliers') : 
             isSelectSupplierPhase ? t('common.select_supplier_comparison') : 
             t('common.rfq_comparison')} - {request.item}
          </h2>
          <p className="rfq-modal-subtitle">
            {t('common.request_id')}: {request.id} | {t('common.status')}: {request.status}
            {isFinalPhase && <span className="final-phase-badge">{t('common.admin_approved')}</span>}
            {isSelectSupplierPhase && <span className="select-supplier-badge">{t('common.select_supplier_phase')}</span>}
          </p>
        </div>

        <div className="rfq-comparison-container">
          <div className="rfq-table-wrapper">
            <table className="rfq-comparison-table">
              <thead>
                <tr>
                  <th className="metric-column">{t('common.metrics')}</th>
                  {displaySuppliers.map((supplier: any, index: number) => (
                    <th key={supplier.supplier_id || supplier.id || index} className={`supplier-column ${supplier.is_selected ? 'selected-supplier' : ''}`}>
                      <div className="supplier-header">
                        {/* Radio buttons removed - read-only mode */}
                        <span className="supplier-name">
                          {supplier.supplier_name || supplier.company_name || supplier.name}
                          {supplier.is_selected && <span className="selected-badge">✓ {t('common.suggested')}</span>}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredComparisonData.map((metric, index) => (
                  <tr key={`metric-${String(metric.key)}-${index}`} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                    <td className="metric-label">{metric.label}</td>
                    {displaySuppliers.map((supplier: any, supplierIndex: number) => (
                      <td key={`${supplier.supplier_id || supplier.id || supplierIndex}-${String(metric.key)}-${supplierIndex}`} className="metric-value">
                        {metric.type === 'documents' ? (
                          <div className="documents-cell">
                            {metric.getValue(supplier).length > 0 ? (
                              <div className="documents-list">
                                {metric.getValue(supplier).map((doc: any, docIndex: number) => {
                                  console.log('🔍 RFQComparisonModal - Document debug:', { doc });
                                  
                                  // Handle documents with download endpoint
                                  const documentId = doc._id || doc.id;
                                  if (documentId) {
                                    return (
                                      <div key={docIndex} className="document-item">
                                        <button 
                                          className="document-link"
                                          onClick={async () => {
                                            try {
                                              console.log('🔍 DEBUG: Download clicked for document:', doc);
                                              const response = await fetch(`${API_ENDPOINTS.DOWNLOAD_SUPPLIER_DOCUMENT(documentId)}?token=${token}`);
                                              
                                              if (response.ok) {
                                                const blob = await response.blob();
                                                const url = window.URL.createObjectURL(blob);
                                                const link = document.createElement('a');
                                                link.href = url;
                                                link.download = doc.original_name || doc.filename || doc.original_filename || 'Document';
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                                window.URL.revokeObjectURL(url);
                                              } else {
                                                console.error('❌ Document download failed:', response.status, response.statusText);
                                                alert('Failed to download document');
                                              }
                                            } catch (error) {
                                              console.error('Download error:', error);
                                              alert('Error downloading document');
                                            }
                                          }}
                                        >
                                          📄 {doc.original_name || doc.filename || doc.original_filename || 'Document'}
                                        </button>
                                      </div>
                                    );
                                  }
                                  
                                  // Fallback for documents without proper ID or URL
                                  return (
                                    <div key={docIndex} className="document-item">
                                      <span className="document-name">
                                        📄 {doc.original_name || doc.filename || doc.original_filename || 'Document'}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="no-documents">No documents</span>
                            )}
                          </div>
                        ) : (
                          metric.getValue(supplier)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rfq-modal-footer">
          <button className="rfq-modal-btn secondary" onClick={onClose}>
            {t('common.close')}
          </button>
          {/* All selection buttons removed - user can only view suppliers now */}
        </div>
      </div>
    </div>
  );
};

export default RFQComparisonModal; 